import { useAtom, useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useLoadingBarState } from '../../../components/LoadingBar/LoadingBarContext';
import { useToastMessageState } from '../../../components/ToastMessage/ToastMessageProvider';
import { ROUTES } from '../../../constants/RouteNameMapping';
import {
  OriginViewDataModel,
  VIEW_TYPE,
  ViewOrder,
  ViewTypeByData,
} from '../../../data-models/view-config.data-model';
import { IViewConfig2, IViewDataModel } from '../../../data-models/view.data-model';
import {
  getTrackableViewId,
  serializeView,
  serializeViewForCreation,
  ViewModel,
} from '../../../models/view.model';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import {
  deleteView,
  duplicateView,
  shareView,
  unshareView,
  updateView,
} from '../../../services/queries/MaggieViewQueries';
import { LoadingId } from '../../../types';
import { buildUniqueName, nameDuplicate } from '../../../util/buildUniqueName';
import { trackEvent } from '../../../util/datadogTracking';
import { useGetMprGridState } from '../MultiPeriodReporting/useHandleGridStateChange';
import { savedMprViewConfigAtom } from '../MultiPeriodReporting/useMprColumnConfigs';
import { getShowSeparateMprRoute } from '../../TempMpr/TempMprWrapper';
import { pageConfigPFJ } from '../state/PageStateJ';
import { isFirstRenderDonePFJ } from '../state/UIStateJ';
import { selectedViewPFJ, viewsPFJ } from '../state/ViewStateJ';

const defaultUpdateWarningShown = new Set<number>();

export function useDeleteView() {
  const navigate = useNavigate();
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const [pageConfig, setPageConfig] = useAtom(pageConfigPFJ);
  const [views, setViews] = useAtom(viewsPFJ);

  return useCallback(
    async (viewToDelete: OriginViewDataModel) => {
      actions.startLoading(LoadingId.deleteView);

      try {
        const viewsAfterDelete = views.filter((view) => view.id !== viewToDelete.id);
        const newOrder = viewsAfterDelete.map((v) => v.viewId);

        await deleteView(viewToDelete);

        pushSuccessToast({ message: 'View deleted successfully.' });
        setViews(viewsAfterDelete);
        setPageConfig({ ...pageConfig, order: newOrder });
        const nextPath =
          viewsAfterDelete.length > 0
            ? `/${ROUTES.PORTFOLIO}/${viewsAfterDelete[0].id}`
            : `/${ROUTES.PORTFOLIO}`;
        navigate(nextPath);
      } catch (err) {
        pushErrorToast({ message: 'Failed to delete view.' });
      }

      actions.stopLoading(LoadingId.deleteView);
    },
    [actions, navigate, pageConfig, pushErrorToast, pushSuccessToast, setPageConfig, setViews, views]
  );
}

export function useDuplicateView() {
  const navigate = useNavigate();
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const [pageConfig, setPageConfig] = useAtom(pageConfigPFJ);
  const [views, setViews] = useAtom(viewsPFJ);
  const getMprViewSettings = useGetMprView();

  return useCallback(
    async (viewToDuplicate: OriginViewDataModel) => {
      actions.startLoading(LoadingId.duplicateView);

      try {
        const id = window.crypto.randomUUID();
        const currentViewNames = views.map((view) => view.label);
        const label = nameDuplicate(viewToDuplicate.label, currentViewNames);
        let newView = {
          ...viewToDuplicate,
          origin: { ...viewToDuplicate },
          viewType: viewToDuplicate.viewType,
          type: VIEW_TYPE.PERSONAL,
          viewId: `${VIEW_TYPE.PERSONAL}_${id}`,
          label,
          name: label,
        };

        if (viewToDuplicate.viewType === ViewTypeByData.multiperiod) {
          newView = { ...newView, ...getMprViewSettings(viewToDuplicate) };
        }

        const payload = serializeViewForCreation(newView);

        const duplicate = await duplicateView(payload);

        const newViews = [...views, duplicate];
        const newOrder = [...pageConfig.order, duplicate.viewId];

        pushSuccessToast({ message: 'New view created.' });
        trackEvent('grid.view_saved', { view_type: newView.type });
        // FD-3125: duplication is a genuine view-creation path. Source all attrs
        // from `newView` (the authoritative new personal view); getTrackableViewId
        // collapses its per-user `personal_<uuid>` to the low-cardinality
        // `personal` bucket.
        trackEvent('portfolio.view_created', {
          view_id: getTrackableViewId(newView),
          view_type: newView.type,
          view_data_type: newView.viewType,
        });
        setViews(newViews);
        setPageConfig({ ...pageConfig, order: newOrder });
        navigate(`/${ROUTES.PORTFOLIO}/${duplicate.id}`);
      } catch (err) {
        pushErrorToast({ message: 'Failed to duplicate view.' });
      }

      actions.stopLoading(LoadingId.duplicateView);
    },
    [
      actions,
      getMprViewSettings,
      navigate,
      pageConfig,
      pushErrorToast,
      pushSuccessToast,
      setPageConfig,
      setViews,
      views,
    ]
  );
}

export function useRenameView() {
  const updateView = useUpdateView();
  const views = useAtomValue(viewsPFJ);

  return useCallback(
    (view: ViewModel, newName: string) => {
      const currentViewNames = views.map((view) => view.label);
      const newUniqueName = buildUniqueName(
        newName,
        currentViewNames.filter((name) => name !== newName)
      );

      return updateView(
        { ...view, label: newUniqueName, name: newUniqueName },
        'View renamed to ' + newUniqueName,
        'Failed to rename view!'
      );
    },
    [updateView, views]
  );
}

export function useShareView() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const [views, setViews] = useAtom(viewsPFJ);

  return useCallback(
    async (viewToUpdate: ViewModel, emails: string[]) => {
      actions.startLoading(LoadingId.updateView);

      let success = true;

      try {
        await shareView(viewToUpdate.id, emails);

        const updateViewModel: ViewModel = {
          ...viewToUpdate,
          sharing: [...(viewToUpdate.sharing ?? []), ...emails],
        };
        const updatedViews = views.map((view) => (view.id === updateViewModel.id ? updateViewModel : view));

        pushSuccessToast({ message: 'Successfully shared view!' });
        setViews(updatedViews);
      } catch (err) {
        pushErrorToast({ message: 'Failed to share view!' });
        success = false;
      }

      actions.stopLoading(LoadingId.updateView);

      return success;
    },
    [actions, pushErrorToast, pushSuccessToast, setViews, views]
  );
}

export function useUnShareView() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();
  const [views, setViews] = useAtom(viewsPFJ);

  return useCallback(
    async (viewToUpdate: ViewModel, emailsToRemove: string[]) => {
      actions.startLoading(LoadingId.updateView);

      const updateViewModel: ViewModel = {
        ...viewToUpdate,
        sharing: viewToUpdate.sharing?.filter((sharedUser) => emailsToRemove.indexOf(sharedUser) < 0),
      };
      const updatedViews = views.map((view) => (view.id === updateViewModel.id ? updateViewModel : view));
      let success = true;

      try {
        await unshareView(viewToUpdate.id, emailsToRemove);
        pushSuccessToast({ message: 'Successfully un-shared view!' });
        setViews(updatedViews);
      } catch (err) {
        pushErrorToast({ message: 'Failed to un-share view!' });
        success = false;
      }

      actions.stopLoading(LoadingId.updateView);

      return success;
    },
    [actions, pushErrorToast, pushSuccessToast, setViews, views]
  );
}

export function useResetView() {
  const updateView = useUpdateView();

  return useCallback(
    (view: ViewModel) => {
      const viewReset: ViewModel = {
        ...view,
        origin: view.origin,
        sections: view.origin?.sections ?? {},
        order: view.origin?.order ?? ({} as ViewOrder),
        filteredFunds: view.origin?.filteredFunds ?? null,
        gridState: view.origin?.gridState,
      };

      return updateView(viewReset, 'Successfully reset view!', 'Failed to reset view!');
    },
    [updateView]
  );
}
export const companyViewChangeTitle = 'COMPANY VIEW WAS CHANGED';
export const sharedViewChangeTitle = 'SHARED VIEW WAS CHANGED';
export const companyViewChangeMessage =
  'You can reset it to default settings or save as a new view. If you leave this page, the data will be automatically reset to the default view.';
export const sharedViewChangeMessage =
  'You can save it as a new view. If you leave this page all the changes will be lost.';
export function useUpdateSelectedView() {
  const isFirstRenderDone = useAtomValue(isFirstRenderDonePFJ);
  const { pushWarningToast } = useToastMessageState();
  const isViewSavingDisabled = getShowSeparateMprRoute();

  return useAtomCallback(
    useCallback(
      async (get, set, updates: Partial<ViewModel>) => {
        const selectedView = await get(selectedViewPFJ);
        if (!selectedView) {
          return;
        }
        const updatedView = {
          ...selectedView,
          ...updates,
        };
        const viewType = updatedView.type;

        const currentViews = await get(viewsPFJ);

        let currentView: ViewModel | undefined;
        const newViews = currentViews.map((view) => {
          if (view.id === updatedView.id) {
            currentView = view;
            return updatedView;
          }
          return view;
        });

        const changed = hasViewChanged(currentView, updatedView);
        if (!changed) {
          return;
        }
        const oldViews = currentViews;
        set(viewsPFJ, newViews);

        if (!canUpdateView(updatedView)) {
          // save state locally, but don't update the view
          return;
        } else if (viewType === VIEW_TYPE.PERSONAL) {
          try {
            const result = await updateView(serializeView(updatedView));
            trackEvent('grid.view_saved', { view_type: viewType });
            return result;
          } catch (err) {
            set(viewsPFJ, oldViews);
          }
        } else if (showWarningToast(updatedView, isFirstRenderDone) && !isViewSavingDisabled) {
          defaultUpdateWarningShown.add(updatedView.id);
          pushWarningToast({
            title: viewType === VIEW_TYPE.COMPANY ? companyViewChangeTitle : sharedViewChangeTitle,
            message: viewType === VIEW_TYPE.COMPANY ? companyViewChangeMessage : sharedViewChangeMessage,
          });
        }
      },
      [isFirstRenderDone, isViewSavingDisabled, pushWarningToast]
    )
  );
}

function canUpdateView(view: ViewModel) {
  if (view.viewType === ViewTypeByData.multiperiod) {
    return PermissionService.get().hasPermission(PermissionKey.canWriteMultiperiod);
  }
  return true;
}

function showWarningToast(view: ViewModel, isFirstRenderDone?: boolean) {
  return (
    !defaultUpdateWarningShown.has(view.id) &&
    (isFirstRenderDone || view.viewType === ViewTypeByData.multiperiod)
  );
}

export function useUpdateView() {
  const { actions } = useLoadingBarState();
  const { pushErrorToast, pushSuccessToast } = useToastMessageState();

  return useAtomCallback(
    useCallback(
      async (
        get,
        set,
        updatedView: ViewModel,
        successMessage = 'View updated!',
        errorMessage = 'Failed to update view'
      ) => {
        actions.startLoading(LoadingId.updateView);

        const updateViewDataModel: IViewDataModel = serializeView(updatedView);

        try {
          const updatedView = await updateView(updateViewDataModel);
          const views = await get(viewsPFJ);
          const newViews = views.map((view) => (view.id === updatedView.id ? updatedView : view));
          pushSuccessToast({ message: successMessage });
          set(viewsPFJ, newViews);
          trackEvent('grid.view_saved', { view_type: updatedView.type });
        } catch (err) {
          pushErrorToast({ message: errorMessage });
        }

        actions.stopLoading(LoadingId.updateView);
      },
      [actions, pushErrorToast, pushSuccessToast]
    )
  );
}

function hasViewChanged(currentView?: ViewModel, updatedView?: ViewModel) {
  return (
    currentView &&
    updatedView &&
    JSON.stringify(serializeView(currentView)) !== JSON.stringify(serializeView(updatedView))
  );
}

function useGetMprView() {
  const getMprGridState = useGetMprGridState();
  return useAtomCallback(
    useCallback(
      (get, _set, viewToDuplicate: OriginViewDataModel) => {
        // current state might not be saved in view.config for default/shared views, so get it from state
        const multiPeriodReportingConfig = get(savedMprViewConfigAtom(viewToDuplicate.id));
        if (!multiPeriodReportingConfig) {
          throw new Error('Missing multi-period reporting config');
        }
        const res: IViewConfig2 = { multiPeriodReportingConfig };
        const gridState = getMprGridState();
        if (gridState) {
          res.gridState = gridState;
        }
        return res;
      },
      [getMprGridState]
    )
  );
}
